import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation, useCurrentLanguage } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'

const Team = () => {
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const language = useCurrentLanguage()
  const location = useLocation()
  const members = t('home.team.members') || []
  const langPrefix = language === 'pt' ? '/pt' : language === 'en' ? '/en' : ''

  usePageSeo({
    enabled: !isLoading,
    title: t('home.seo.team.title'),
    description: t('home.seo.team.description'),
    keywords: t('home.seo.team.keywords'),
    pathname: location.pathname
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite">
        <main className="py-16 px-4">
          <div className="max-w-6xl mx-auto animate-pulse space-y-4">
            <div className="h-10 bg-gray-200 rounded w-1/2 mx-auto" />
            <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="bg-gray-200 rounded-xl aspect-[3/4]" />
              ))}
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite">
      <main className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="text-center mb-10 md:mb-14">
            <h1 className="text-3xl md:text-4xl font-bold text-primary-darkGreen mb-4">
              {t('home.team.heading')}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {t('home.team.description')}
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {members.map((member) => (
              <Link
                key={member.slug}
                to={`${langPrefix}/team/${member.slug}`}
                className="group block w-full max-w-[220px] sm:max-w-[240px] md:max-w-[252px] mx-auto rounded-xl overflow-hidden ring-2 ring-black/10 shadow-md shadow-black/25 transition-[box-shadow,ring-color] duration-300 ease-out hover:ring-primary-darkGreen/45 hover:shadow-lg hover:shadow-black/40"
              >
                <div
                  className="aspect-[3/4] relative bg-neutral-200 overflow-hidden"
                  style={member.cardBackdrop ? { backgroundColor: member.cardBackdrop } : undefined}
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className={
                      member.cardPhotoInset
                        ? 'absolute inset-x-0 top-0 w-full h-[72%] object-cover object-top'
                        : 'absolute inset-0 w-full h-full object-cover object-top'
                    }
                  />
                  <div className="absolute inset-x-3 bottom-3 sm:inset-x-4 sm:bottom-4 bg-white px-3 py-2.5 sm:py-3 shadow-md rounded-sm text-left pointer-events-none">
                    <h2 className="text-sm sm:text-base font-bold text-primary-darkGreen leading-snug">{member.name}</h2>
                    {member.cardTitle || member.title ? (
                      <p className="text-xs sm:text-[13px] text-gray-500 mt-1 leading-snug">{member.cardTitle || member.title}</p>
                    ) : null}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}

export default Team
